/**
 * Where a room starts (`/rooms/new`): "What are you making?", a recording or a
 * live room, then the first steps before there is a draft.
 *
 * A recording's step 1 takes the talk; Start mints the draft and hands the file
 * to the board it minted, which does the uploading and the rest of the steps. A
 * live room's steps 1 and 2 (when, and what it will cover) are filled here; Write
 * the page mints the draft and its board writes the page from the notes.
 *
 * Which way the host went, and what they typed for a live, survives a reload.
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { LIVE_LENGTHS, liveFileKind } from "../../shared/live-entry";
import { useAuth } from "../auth/AuthContext";
import { GoLiveSheet } from "../components/GoLiveSheet";
import { useLayout } from "../components/season/layout";
import { Icon } from "../components/ui/Icon";
import { Pill } from "../components/ui/Pill";
import { SAMPLE_ART } from "../lib/sample-art";
import { openAssets } from "../assets/store";
import { Select } from "../components/ui/Select";
import { TimeZonePicker } from "../components/TimeZonePicker";
import { canStartLive, dayLabel, startTimeGroups, timeLabel, todayIn, type LiveDraft } from "./liveForm";
import { boardPath } from "./paths";
import { MakeFrame, StepHead } from "./make/MakeFrame";
import { StepUpload } from "./make/MakeSteps";
import { endsLine, RepeatPicker, repeatGuestLine } from "./RepeatPicker";
import { SeasonLiveStart, SeasonNewRoom, SeasonRecordingStart } from "./season/SeasonStart";
import { lengthLine, lengthWords, useLiveStart, useRecordingStart } from "./useStart";
import "../styles/make.css";

type Way = "pick" | "recording" | "live";

function wayOf(v: string | null): Way {
  return v === "recording" || v === "live" ? v : "pick";
}

export function BoardEntryView() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const layout = useLayout();
  const way = wayOf(params.get("from"));
  const started = (id: string) => navigate(boardPath(id), { replace: true });
  // "Make a room from this" in Assets: the room starts from that talk at once.
  const asset = params.get("asset");
  if (layout !== "computer") {
    if (asset) return <SeasonRecordingStart layout={layout} onStarted={started} from={asset} />;
    if (way === "recording") return <SeasonRecordingStart layout={layout} onStarted={started} />;
    if (way === "live") return <SeasonLiveStart layout={layout} onStarted={started} />;
    return <SeasonNewRoom layout={layout} />;
  }
  if (asset) return <RecordingStart onStarted={started} from={asset} />;
  if (way === "recording") return <RecordingStart onStarted={started} />;
  if (way === "live") return <LiveStart onStarted={started} />;
  return <NewRoom onPick={(next) => setParams({ from: next })} />;
}

/** "What are you making?" */
function NewRoom({ onPick }: { onPick: (way: Exclude<Way, "pick">) => void }) {
  const { user } = useAuth();
  const [goingLive, setGoingLive] = useState(false);
  return (
    <div className="page nbnew">
      <Link className="back" to="/rooms">
        <Icon name="left" /> Rooms
      </Link>
      <div className="ph">
        <div>
          <h1>What are you making?</h1>
        </div>
      </div>
      <div className="choose">
        <button type="button" className="card choice" onClick={() => onPick("recording")}>
          <div className="poster">
            <img src={SAMPLE_ART.talk} alt="" />
            <div className="ov">
              <Pill dot>A recording</Pill>
            </div>
          </div>
          <div className="cb">
            <h2>A recording</h2>
            <p>A talk you have already recorded.</p>
            <ol>
              <li>Upload</li>
              <li>We make it playable</li>
              <li>We listen and write your page</li>
              <li>You check and publish</li>
            </ol>
            <span className="btn p lg mt8 nbgo">
              Start with a recording <Icon name="right" />
            </span>
          </div>
        </button>
        <button type="button" className="card choice" onClick={() => onPick("live")}>
          <div className="poster">
            <img src={SAMPLE_ART.live} alt="" />
            <div className="ov">
              <Pill dot className="nblive">
                A live room
              </Pill>
            </div>
          </div>
          <div className="cb">
            <h2>A live room</h2>
            <p>Live on a date, once or every week.</p>
            <ol>
              <li>When</li>
              <li>What it is about</li>
              <li>We write the page</li>
              <li>Design the page</li>
            </ol>
            <span className="btn p lg mt8 nbgo">
              Start a live room <Icon name="right" />
            </span>
          </div>
        </button>
      </div>
      <div className="note mt24 golive">
        <Icon name="live" />
        <span>Need to talk to people right now?</span>
        <button type="button" className="btn s live" onClick={() => setGoingLive(true)}>
          <span className="dot" />
          Go live now
        </button>
      </div>
      {goingLive ? <GoLiveSheet hostFirst={user?.first_name ?? ""} onClose={() => setGoingLive(false)} onMade={() => setGoingLive(false)} /> : null}
    </div>
  );
}

/**
 * A recording's step 1: the talk, picked (or uploaded) in Assets. The pick mints
 * the draft and hands the talk on. `from` is a talk already chosen in Assets.
 */
function RecordingStart({ onStarted, from }: { onStarted: (webinarId: string) => void; from?: string }) {
  const { starting, error, start } = useRecordingStart(onStarted, from);
  return (
    <MakeFrame kind="rec" current={0} reached={0} lines={[]} started={false}>
      <StepUpload
        onTalk={(a) => {
          if (!starting) start({ id: a.id, name: a.name, size: a.size_bytes });
        }}
        error={error}
        busy={starting}
      />
    </MakeFrame>
  );
}

/** When: the day, the start, how long, the zone, and whether it repeats. */
function WhenForm({ draft, onChange }: { draft: LiveDraft; onChange: (patch: Partial<LiveDraft>) => void }) {
  const today = todayIn(draft.timezone);
  return (
    <>
      <div className="grid g2 nbwhen">
        <div className="field">
          <label htmlFor="nb-day">Day</label>
          <input id="nb-day" className="inp" type="date" min={today} value={draft.date} onChange={(e) => onChange({ date: e.target.value })} />
        </div>
        <div className="field">
          <span className="lbl">Starts</span>
          <Select
            ariaLabel="Starts"
            placeholder="Pick a time"
            value={draft.time}
            suggested="09:00"
            options={startTimeGroups(draft.date, draft.timezone)}
            onChange={(time) => onChange({ time })}
          />
        </div>
        <div className="field">
          <span className="lbl">For</span>
          <Select
            ariaLabel="For"
            value={String(draft.lengthMin)}
            options={LIVE_LENGTHS.map((l) => ({ value: String(l.min), label: lengthWords(l.min) }))}
            onChange={(min) => onChange({ lengthMin: Number(min) })}
          />
        </div>
        <div className="field">
          <span className="lbl">Time zone</span>
          <TimeZonePicker className="nbzone" value={draft.timezone} onChange={(timezone) => onChange({ timezone })} />
        </div>
      </div>
      <div className="field mt20">
        <span className="lbl">Repeats</span>
        <RepeatPicker
          when={{ date: draft.date, time: draft.time, timezone: draft.timezone }}
          value={draft.pattern}
          onChange={(pattern) => onChange({ pattern })}
        />
      </div>
    </>
  );
}

/** A live room's steps 1 and 2: when, and what it will cover. Write the page mints the draft. */
function LiveStart({ onStarted }: { onStarted: (webinarId: string) => void }) {
  const { draft, set, step, setStep, file, setFile, refused, take, starting, error, at, passed, problem, whenOk, whenLine, short, write } =
    useLiveStart(onStarted);

  const day = draft.date ? new Date(`${draft.date}T12:00:00`) : null;
  const guest = draft.pattern && draft.date ? repeatGuestLine(draft.pattern, draft) : "";

  return (
    <MakeFrame kind="live" current={step} reached={step} lines={[whenLine, "Your notes"]} started={false}>
      {step === 0 ? (
        <div className="nbtwo">
          <div className="card nbw">
            <StepHead n={1} of={4} title="When is it?" />
            <WhenForm draft={draft} onChange={set} />
            {at ? (
              <div className="nbwhensum mt20" role={passed || problem ? "alert" : undefined}>
                {passed || problem ? (
                  <>
                    <span className="o bad">
                      <Icon name="info" />
                    </span>
                    <div>
                      <b>{passed ? "That time has already passed." : problem}</b>
                      <span>Pick a later one.</span>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="o">
                      <Icon name="cal" />
                    </span>
                    <div>
                      <b>{whenLine}</b>
                      <span>
                        {lengthLine(draft)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            ) : null}
            <div className="nbw-f">
              <button type="button" className="btn p lg" disabled={!whenOk} onClick={() => setStep(1)}>
                Continue <Icon name="right" />
              </button>
            </div>
          </div>
          <aside className="nbpeek">
            <div className="lbl">What people will see</div>
            <div className="pv nbpeekcard">
              <div className="nb-date">
                <span>{day ? day.toLocaleDateString("en-US", { weekday: "short" }) : "Day"}</span>
                <b>{day ? day.getDate() : "?"}</b>
                <span>{day ? day.toLocaleDateString("en-US", { month: "short" }) : ""}</span>
              </div>
              <div className="nbpeek-b">
                <b className="ph1">{draft.date ? dayLabel(draft.date) : "Pick a day"}</b>
                <span>
                  {draft.time ? timeLabel(draft.time) : "Pick a time"} · {draft.lengthMin} min
                </span>
                {guest ? (
                  <span className="nb-series">
                    <b>{guest}</b>
                    {draft.pattern && draft.pattern.ends.kind !== "never" ? <span>{endsLine(draft.pattern, draft.date)}</span> : null}
                  </span>
                ) : null}
                <div className="pin">First name</div>
                <div className="pin">Email</div>
                <div className="pbtn">Save your seat</div>
                <small>{draft.pattern ? "One seat for every session." : "Can’t make it live? The replay lands on your page."}</small>
              </div>
            </div>
          </aside>
        </div>
      ) : (
        <div className="nbtwo">
          <div className="card nbw">
            <StepHead n={2} of={4} title="What will you cover?" />
            <div className="field">
              <textarea dir="auto"
                className="inp nbnotes"
                rows={6}
                aria-label="What will you cover?"
                value={draft.notes}
                onChange={(e) => set({ notes: e.target.value })}
                placeholder="For example: A live Q&A on the winter market. I’ll cover what sold this fall, whether to list now or wait for spring, and take your questions until the hour is up."
              />
            </div>
            <div className="nbattach">
              {file ? (
                <div className="nbfilechip">
                  <i className="kind">{liveFileKind(file.name, file.mime) === "pdf" ? "PDF" : "TXT"}</i>
                  <span className="sp">
                    <b>{file.name}</b>
                  </span>
                  <button type="button" className="ibtn sm" aria-label="Take the file off" onClick={() => setFile(null)}>
                    <Icon name="x" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn s"
                  onClick={() => openAssets({ fits: "notes", title: "Notes or slides", cta: "Use this file", onPick: take })}
                >
                  <Icon name="clip" /> Notes or slides
                </button>
              )}
            </div>
            <div className={short || refused ? "hint mt8 nbwarn" : "hint mt8"}>{refused || (short ? "A few more words" : "PDF or text, up to 10 MB")}</div>
            {error ? (
              <p className="note warn mt16" role="alert">
                <Icon name="info" />
                <span>{error}</span>
              </p>
            ) : null}
            <div className="nbw-f">
              <button type="button" className="btn q" onClick={() => setStep(0)}>
                <Icon name="left" /> Back
              </button>
              <button type="button" className="btn p lg" disabled={starting || !canStartLive(draft)} onClick={() => void write()}>
                <Icon name="sparkle" /> {starting ? "Starting…" : "Write the page"}
              </button>
              <span className="small muted">About a minute.</span>
            </div>
          </div>
          <aside className="nbpeek">
            <div className="lbl">What happens next</div>
            <ol className="nbnext">
              <li>
                <b>We read your notes</b>
              </li>
              <li>
                <b>We write the page</b>
              </li>
              <li>
                <b>You pick a design</b>
              </li>
              <li>
                <b>Your board</b>
              </li>
            </ol>
          </aside>
        </div>
      )}
    </MakeFrame>
  );
}
